import * as React from "react";
import { cloneDeep } from "lodash";
import PerfectScrollbar from "perfect-scrollbar-react";
import { toastr } from "react-redux-toastr";
import ReactHtmlParser from "react-html-parser";
import { Button } from "reactstrap";
import SidebarContainer from "src/modules/Routing/Sidebar/SidebarContainer";
import GSChildInformation from "./components/GSChildInformation";
import GSCaseInformation from "./components/GSCaseInformation";
import GSImageList from "./components/GSImageList";
import GSLoading from "./components/GSLoading";
import GSResultsInformation from "./components/GSResultsInformation";
import { GeoSearchService } from "src/share/services/geoSearch";
import { IGSResultProps, IGSResultState, initialGeoSearchResult, initialGeoSearchState } from "src/share/types/geoSearch";
import { timestampToLongDate, momentAgeNow } from "src/share/utils/utils";
import MasonrySkeleton from "src/share/components/Masonry/MasonrySkeleton";
import { renderLoadedText } from "src/share/utils/placeholders";
import routes from "src/modules/Routing/routes";
import "./GeoSearchResult.css";
import { RoutesModules } from "src/modules/Routing/types";

const PHOTO_NOT_FOUND = "https://s3.us-east-2.amazonaws.com/caseview-children-assets/Images/no_photo_found.jpg";

export class GeoSearchResult extends React.PureComponent<IGSResultProps, IGSResultState> {
  public constructor(props) {
    super(props);
    this.state = {
      processedImages: {
        total: 0,
        uploaded: 0,
        percentage: 0
      },
      processedImagesInterval: 0,
      isProcessing: false,
      isLoadingInfo: true,
      isLoadingImages: true,
      isFetchingImages: false,
      page: 0,
      size: 50,
      result: cloneDeep(initialGeoSearchResult),
      images: [],
      loadedImages: []
    };
  }
  public async componentDidMount() {
    const { searchId } = this.props.match.params;
    try {
      const processedImages = await GeoSearchService.getProcessedImages(searchId);
      if (processedImages.total > 0 && processedImages.percentage !== 100) {
        await this.fetchResultInfo(searchId);
        const processedImagesInterval = setInterval(async () => await this.getGeoSearchProcessState(searchId), 10000);
        this.setState({ processedImages, processedImagesInterval, isProcessing: true });
      } else {
        await this.fetchResultInfo(searchId);
        await this.fetchResultImages(searchId);
      }
    } catch (error) {
      toastr.error("Error", error.payload);
      if (error.status === 404) {
        this.props.history.push(`/not-found`);
      }
    }
  }
  public componentWillUnmount() {
    clearInterval(this.state.processedImagesInterval);
  }
  private getGeoSearchProcessState = async (searchId: string) => {
    const processedImages = await GeoSearchService.getProcessedImages(searchId);
    if (processedImages.percentage === 100) {
      clearInterval(this.state.processedImagesInterval);
      await this.fetchResultImages(searchId);
      await this.fetchResultInfo(searchId);
      this.setState({ isProcessing: false });
    }
    this.setState({ processedImages });
  };
  private async fetchResultInfo(searchId: string) {
    const result = await GeoSearchService.getById(searchId);
    this.setState({ result, isLoadingInfo: false });
  }
  private fetchResultImages = async (searchId: string) => {
    const images = await GeoSearchService.getResultImages(searchId, this.state.page, this.state.size);
    this.setState({ images: [...this.state.images, ...images], page: this.state.page + 1, isLoadingImages: false });
  };
  private getLocation = () => {
    const { country, state, city } = this.state.result;
    return `${city ? `${city}, ` : ""}${state ? `${state}, ` : ""}${country}.`;
  };;
  private formatAge = (date) => `${momentAgeNow(date)} years`;
  private handleRedirectToPhotoDetails = (photoId) => {
    this.props.history.push(`${routes.GEOSEARCH_PHOTO.replace(":photoId", photoId)}`);
  };
  private handleLoadMore = async () => {
    const { searchId } = this.props.match.params;
    this.setState({ isFetchingImages: true });
    await this.fetchResultImages(searchId);
    this.setState({ isFetchingImages: false });
  };
  private handleImageError = (evt) => {
    evt.target.onerror = null;
    evt.target.src = PHOTO_NOT_FOUND;
  };
  private clickEdit = (evt) => {
    this.props.history.push({
      pathname: this.state.result.manual ? routes.GEOSEARCH_MANUAL_FORM : routes.GEOSEARCH_FORM,
      state: this.getFormState()
    })
  }
  private getFormState = () => {
    const { caseId, dateRange, center, radius, personId } = this.state.result
    const initial = cloneDeep(initialGeoSearchState)
    const state = {
      ...initial,
      caseInfo: {
        ...initial,
        caseId
      },
      dateRange: {
        startDate: dateRange.gte || null,
        endDate: dateRange.lte || null
      },
      selectedLocation: center,
      radius,
      selectedChild: personId
    }
    return state;
  }
  public render() {
    const { isLoadingInfo, isLoadingImages, isFetchingImages, isProcessing, result: { children } } = this.state;
    const { fullName = '', birthDate = "", sex = "" } = children && children.length > 0 && children[0] || {};
    const wereResultsFound = !isLoadingImages && this.state.images.length > 0;
    return (
      <div className="geosearch-result__content">
        <PerfectScrollbar>
          <div className="geosearch-result__container white-background">
            <div className="geosearch-result__search-info">
              <h1>Search Information</h1>
              <GSResultsInformation
                results={renderLoadedText(this.state.result.results || "0", !isLoadingInfo)}
                faces={renderLoadedText(this.state.result.faces || "0", !isLoadingInfo && !isProcessing)}
                matches={renderLoadedText(this.state.result.matches || "0", !isLoadingInfo && !isProcessing)}
                center={this.state.result.center}
                from={renderLoadedText(this.state.result.dateRange.gte, !isLoadingInfo, 1, 100, timestampToLongDate)}
                to={renderLoadedText(this.state.result.dateRange.lte, !isLoadingInfo, 1, 100, timestampToLongDate)}
                radius={renderLoadedText(this.state.result.radius, !isLoadingInfo)}
                loading={isLoadingInfo}
              />
              {this.state.result.requestedImageURL &&
                <div className="geosearch-result__processed-image">
                  <h2>Processed Image</h2>
                  <img src={this.state.result.requestedImageURL} alt="Processed Image" />
                </div>
              }
              {!this.state.result.manual &&
                <GSChildInformation
                  name={renderLoadedText(fullName, !isLoadingInfo, 1, 75)}
                  age={renderLoadedText(birthDate, !isLoadingInfo, 1, 25, this.formatAge)}
                  sex={renderLoadedText(sex, !isLoadingInfo, 1)}
                  missingDate={renderLoadedText(this.state.result.missingDate, !isLoadingInfo, 1, 75, timestampToLongDate)}
                />}
              {!this.state.result.manual &&
                <GSCaseInformation
                  caseId={renderLoadedText(this.state.result.caseId, !isLoadingInfo, 1, 75)}
                  location={renderLoadedText(this.getLocation(), !isLoadingInfo, 1, 75)}
                  openDate={renderLoadedText(this.state.result.openDate, !isLoadingInfo, 1, 75, timestampToLongDate)}
                  agency={renderLoadedText(this.state.result.agencyCode, !isLoadingInfo, 1, 75)}
                  circumstances={renderLoadedText(this.state.result.circumstances, !isLoadingInfo, 3, 100, ReactHtmlParser)}
                  contactInformation={renderLoadedText(this.state.result.contactInformation, !isLoadingInfo, 3, 100, ReactHtmlParser)}
                />}
            </div>
            <div className="geosearch-result__images">
              <h1>Search Results</h1>
              <div className="geosearch-result__images-collection">
                {!isLoadingImages && !isProcessing && !wereResultsFound && <h5 className="geosearch-result__not-found">No results were found.</h5>}
                {isProcessing ?
                  <GSLoading
                    uploaded={this.state.processedImages.uploaded}
                    total={this.state.processedImages.total}
                    percentage={this.state.processedImages.percentage}
                  /> :
                  <PerfectScrollbar>
                    {isLoadingImages && !isProcessing && <MasonrySkeleton />}
                    {!isLoadingImages && !isProcessing &&
                      <GSImageList
                        images={this.state.images}
                        totalResults={this.state.result.results}
                        isFetching={isFetchingImages}
                        onImageError={this.handleImageError}
                        onRedirect={this.handleRedirectToPhotoDetails}
                        onLoadMore={this.handleLoadMore}
                      />}

                  </PerfectScrollbar>
                }
              </div>
              {
                !this.state.isLoadingInfo &&
                <Button onClick={this.clickEdit} className="float-right btn btn-secondary btn-green-fill">
                  Edit
              </Button>
              }
            </div>
          </div>
        </PerfectScrollbar>
      </div>
    );
  }
}

export default SidebarContainer(RoutesModules.GEOSEARCH)(GeoSearchResult);


// WEBPACK FOOTER //
// ./src/modules/GeoSearch/Result/GeoSearchResult.tsx